import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PresenceService } from '../services/presence.service';
import { AgentAvailabilityResponse, AgentPresenceStatus } from '../models/call-routing.model';
import { ToastService } from '../../../shared/services/toast.service';
import { AGENT_PRESENCE_LABELS } from '../call-labels';

interface PresenceOption {
  value: AgentPresenceStatus;
  label: string;
  color: string;
  description: string;
}

@Component({
  selector: 'app-agent-presence-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './agent-presence-selector.component.html'
})
export class AgentPresenceSelectorComponent implements OnInit, OnDestroy {
  presence: AgentAvailabilityResponse | null = null;
  open = false;
  updating = false;

  // Las etiquetas salen del diccionario del módulo (call-labels.ts); aquí sólo
  // se añade el color y la descripción propios del selector.
  readonly options: PresenceOption[] = [
    { value: 'AVAILABLE', label: AGENT_PRESENCE_LABELS['AVAILABLE'], color: 'bg-success-500', description: 'Recibo llamadas entrantes' },
    { value: 'BUSY', label: AGENT_PRESENCE_LABELS['BUSY'], color: 'bg-warning-500', description: 'En reunión / no recibir' },
    { value: 'ON_BREAK', label: AGENT_PRESENCE_LABELS['ON_BREAK'], color: 'bg-blue-500', description: 'Descanso / break' },
    { value: 'OFFLINE', label: AGENT_PRESENCE_LABELS['OFFLINE'], color: 'bg-gray-400', description: 'Fuera de horario' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly presenceService: PresenceService,
    private readonly toast: ToastService,
    private readonly elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.presenceService.init();
    this.presenceService.presence$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      this.presence = p;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (!this.open) return;
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  toggle(): void { this.open = !this.open; }

  currentOption(): PresenceOption {
    return this.options.find(o => o.value === this.presence?.presenceStatus) ?? this.options[3];
  }

  select(opt: PresenceOption): void {
    if (this.updating) return;
    if (opt.value === this.presence?.presenceStatus) {
      this.open = false;
      return;
    }
    this.updating = true;
    this.presenceService.update(opt.value, { manual: true }).subscribe({
      next: (res) => {
        this.updating = false;
        this.open = false;
        if (res) {
          this.toast.success({ title: 'Presencia actualizada', message: `Estás ${opt.label.toLowerCase()}.` });
        } else {
          this.toast.error({ title: 'Error', message: 'No se pudo actualizar tu presencia.' });
        }
      }
    });
  }
}
